/**
 * RelationshipsPage — Catálogo · Compatibilidades y relaciones (vista global).
 *
 * Lista todas las relaciones tipadas producto↔producto del catálogo y permite
 * crearlas/eliminarlas. La gestión puntual también vive en la ficha de cada
 * producto; esta página da la vista de conjunto y la carga rápida.
 */
import { useMemo, useState } from 'react'
import {
  Plus,
  Trash2,
  Loader2,
  Link2,
  ArrowRight,
  Pencil,
} from 'lucide-react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { DataTable } from '@/components/ds/DataTable'
import { ConfirmModal } from '@/components/ConfirmModal'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { ToastContainer, useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { useProducts } from '@/modules/sales/hooks/useSales'
import { Modal } from '@/components/ds/Modal'
import {
  useProductRelationships, useCreateProductRelationship, useUpdateProductRelationship,
  useDeleteProductRelationship,
  RELATIONSHIP_TYPES, type RelationshipType, type ProductRelationship,
} from '@/modules/sales/hooks/useCatalog'

function NewRelationshipModal({ open, onClose, productOptions, isPending, onSubmit }: {
  open: boolean
  onClose: () => void
  productOptions: { id: string; label: string }[]
  isPending: boolean
  onSubmit: (data: { from_product: string; to_product: string; relationship_type: RelationshipType; notes: string }) => void
}) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [type, setType] = useState<RelationshipType>('compatible_with')
  const [notes, setNotes] = useState('')

  if (!open) return null

  const fieldCls = 'px-3 py-2 border border-divider rounded-lg bg-surface text-content text-sm w-full focus:border-primary focus:ring-1 focus:ring-primary/50'

  return (
    <Modal open onClose={onClose} title="Nueva relación" size="lg">
      <div className="space-y-4">
        <div>
          <label htmlFor="relationshipspage-producto-origen" className="block text-sm font-medium text-content mb-1.5">Producto origen</label>
          <EntityAutocomplete id="relationshipspage-producto-origen" options={productOptions} value={from} onChange={setFrom}
            placeholder="Buscar producto…" className={fieldCls} />
        </div>
        <div>
          <label htmlFor="relationshipspage-tipo-de-relacion-2" className="block text-sm font-medium text-content mb-1.5">Tipo de relación</label>
          <select id="relationshipspage-tipo-de-relacion-2" value={type} onChange={e => setType(e.target.value as RelationshipType)} className={fieldCls}>
            {RELATIONSHIP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="relationshipspage-producto-destino" className="block text-sm font-medium text-content mb-1.5">Producto destino</label>
          <EntityAutocomplete id="relationshipspage-producto-destino" options={productOptions.filter(p => p.id !== from)} value={to} onChange={setTo}
            placeholder="Buscar producto…" className={fieldCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-content mb-1.5">Notas <span className="text-content-subtle font-normal">(opcional)</span></label>
          <input aria-label="Notas" value={notes} onChange={e => setNotes(e.target.value)} className={fieldCls} placeholder="Ej: rango 2016–2020" />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-divider">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
            Cancelar
          </button>
          <button type="button" disabled={isPending || !from || !to}
            onClick={() => onSubmit({ from_product: from, to_product: to, relationship_type: type, notes })}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
            {isPending && <Loader2 size={14} className="animate-spin" />}Crear relación
          </button>
        </div>
      </div>
    </Modal>
  )
}

function EditRelationshipModal({ rel, onClose, isPending, onSubmit }: {
  rel: ProductRelationship | null
  onClose: () => void
  isPending: boolean
  onSubmit: (data: { relationship_type: RelationshipType; notes: string }) => void
}) {
  const [type, setType] = useState<RelationshipType>(rel?.relationship_type ?? 'compatible_with')
  const [notes, setNotes] = useState(rel?.notes ?? '')

  if (!rel) return null

  const fieldCls = 'px-3 py-2 border border-divider rounded-lg bg-surface text-content text-sm w-full focus:border-primary focus:ring-1 focus:ring-primary/50'

  return (
    <Modal open onClose={onClose} title="Editar relación" size="lg">
      <div className="space-y-4">
        {/* Productos: solo lectura (cambiarlos es otra relación → crear/eliminar) */}
        <div className="flex items-center gap-2 text-sm bg-soft rounded-lg px-3 py-2">
          <span className="text-content">{rel.from_product_name}</span>
          <ArrowRight size={14} className="text-content-subtle shrink-0" />
          <span className="text-content">{rel.to_product_name}</span>
        </div>
        <div>
          <label htmlFor="relationshipspage-tipo-de-relacion" className="block text-sm font-medium text-content mb-1.5">Tipo de relación</label>
          <select id="relationshipspage-tipo-de-relacion" value={type} onChange={e => setType(e.target.value as RelationshipType)} className={fieldCls}>
            {RELATIONSHIP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-content mb-1.5">Notas <span className="text-content-subtle font-normal">(opcional)</span></label>
          <input aria-label="Notas" value={notes} onChange={e => setNotes(e.target.value)} className={fieldCls} placeholder="Ej: rango 2016–2020" />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-divider">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
            Cancelar
          </button>
          <button type="button" disabled={isPending}
            onClick={() => onSubmit({ relationship_type: type, notes })}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
            {isPending && <Loader2 size={14} className="animate-spin" />}Guardar cambios
          </button>
        </div>
      </div>
    </Modal>
  )
}

export function RelationshipsPage() {
  const toast = useToast()
  const { can } = usePermission()
  const canEdit = can('sales.products', 'edit')
  const canCreate = can('sales.products', 'create')

  const { data, isLoading } = useProductRelationships()
  const relationships = data?.relationships ?? []

  const { data: prodData } = useProducts({ active: 'true', page_size: 1000 })
  const productOptions = useMemo(
    () => (prodData?.products ?? []).map(p => ({ id: p.id, label: p.sku ? `${p.name} · ${p.sku}` : p.name })),
    [prodData],
  )

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ProductRelationship | null>(null)
  const [deleting, setDeleting] = useState<ProductRelationship | null>(null)
  const createMut = useCreateProductRelationship()
  const updateMut = useUpdateProductRelationship()
  const deleteMut = useDeleteProductRelationship()

  const handleCreate = (payload: { from_product: string; to_product: string; relationship_type: RelationshipType; notes: string }) => {
    createMut.mutate(payload, {
      onSuccess: () => { setModalOpen(false); toast.success('Relación creada.') },
      onError: () => toast.error('No se pudo crear (¿relación duplicada o producto repetido?).'),
    })
  }

  const handleUpdate = (payload: { relationship_type: RelationshipType; notes: string }) => {
    if (!editing) return
    updateMut.mutate({ id: editing.id, ...payload }, {
      onSuccess: () => { setEditing(null); toast.success('Relación actualizada.') },
      onError: () => toast.error('No se pudo actualizar (¿ya existe ese tipo entre ambos productos?).'),
    })
  }

  const handleDelete = () => {
    if (!deleting) return
    deleteMut.mutate(deleting.id, {
      onSuccess: () => { setDeleting(null); toast.success('Relación eliminada.') },
      onError: () => { setDeleting(null); toast.error('No se pudo eliminar.') },
    })
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Compatibilidades"
        subtitle="Relaciones entre productos: compatibilidad, equivalencias, sustitutos, accesorios y componentes"
        help={HELP['sales.relationships']}
      >
        {canCreate && (
          <button onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={16} />Nueva relación
          </button>
        )}
      </PageHeader>

      <DataTable<typeof relationships[number]>
        columns={[
          { key: 'from_product_name', header: 'Producto origen', sortable: true,
            render: rel => (
              <>
                <p className="text-sm text-content">{rel.from_product_name}</p>
                {rel.from_product_sku && (
                  <p className="text-xs text-content-subtle font-mono">{rel.from_product_sku}</p>
                )}
              </>
            ) },
          { key: 'relationship_type_display', header: 'Relación', sortable: true,
            render: rel => (
              <>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-primary-50 text-primary-hover">
                  {rel.relationship_type_display}<ArrowRight size={11} />
                </span>
                {rel.notes && <p className="text-xs text-content-subtle mt-1">{rel.notes}</p>}
              </>
            ) },
          { key: 'to_product_name', header: 'Producto destino', sortable: true,
            render: rel => (
              <>
                <p className="text-sm text-content">{rel.to_product_name}</p>
                {rel.to_product_sku && (
                  <p className="text-xs text-content-subtle font-mono">{rel.to_product_sku}</p>
                )}
              </>
            ) },
          ...(canEdit ? [{
            key: 'acciones', header: '', width: 88,
            render: (rel: typeof relationships[number]) => (
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => setEditing(rel)}
                  className="p-1.5 rounded text-content-subtle hover:text-primary transition-colors" title="Editar">
                  <Pencil size={14} />
                </button>
                <button onClick={() => setDeleting(rel)}
                  className="p-1.5 rounded text-content-subtle hover:text-danger-content-soft transition-colors" title="Eliminar">
                  <Trash2 size={14} />
                </button>
              </div>
            ),
          }] : []),
        ]}
        rows={relationships}
        rowKey={rel => rel.id}
        minWidth={720}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={6} cols={4} />}
        empty={
          <EmptyState
            icon={Link2}
            title="Sin relaciones"
            hint="Vincula productos compatibles, equivalencias o sustitutos. También se puede hacer desde la ficha de cada producto."
          />
        }
        caption="Relaciones entre productos del catálogo"
      />

      <NewRelationshipModal
        key={modalOpen ? 'open' : 'closed'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        productOptions={productOptions}
        isPending={createMut.isPending}
        onSubmit={handleCreate}
      />

      <EditRelationshipModal
        key={editing?.id ?? 'edit-closed'}
        rel={editing}
        onClose={() => setEditing(null)}
        isPending={updateMut.isPending}
        onSubmit={handleUpdate}
      />

      <ConfirmModal
        open={deleting !== null}
        message={`¿Eliminar la relación "${deleting?.from_product_name}" → "${deleting?.to_product_name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        isPending={deleteMut.isPending}
        variant="danger"
      />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
